import React from 'react';
import { setHours, setMinutes, getHours, getMinutes } from 'date-fns';
import { useAnnouncementsMutation } from '@features/announcements/queries/announcements.mutations';
import { createAnnouncementsApi } from '@features/announcements/api/announcements.api';
import { auth } from '@core/utils/firebase/firebase';
import type { Auth } from '@angular/fire/auth';
import { Announcement, Site } from '@core/interfaces/api';
import type { BulkConfirmationCounts } from '../modals/use-bulk-confirmation-dialog';

type SaveData = any; // shape is driven by SlideForm

interface UseSaveSlideParams {
  siteId: number;
  organizationId: number;
  bulkEnabled: boolean;
  orgSites: Array<Site>;
  existingAnnouncement?: Announcement;
  bulkGroup?: Announcement[];
  confirmBulkChanges?: (counts: BulkConfirmationCounts) => Promise<boolean>;
}

export function useSaveSlide({
  siteId,
  organizationId,
  bulkEnabled,
  orgSites,
  existingAnnouncement,
  bulkGroup = [],
  confirmBulkChanges,
}: UseSaveSlideParams) {
  const normalizedBulkGroup = React.useMemo(() => {
    const cleaned = (bulkGroup || [])
      .filter((announcement: any) => announcement && !announcement.deleted)
      .map((announcement: any) => ({ ...announcement }));
    if (
      existingAnnouncement?.id &&
      !cleaned.some((announcement: any) => announcement.id === existingAnnouncement.id)
    ) {
      cleaned.push(existingAnnouncement);
    }
    return cleaned;
  }, [bulkGroup, existingAnnouncement]);

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const {
    createAnnouncement,
    updateAnnouncement,
    createAnnouncementsBulk,
    updateAnnouncementsBulk,
    deleteAnnouncementsBulk,
  } = useAnnouncementsMutation({ siteId });

  // Imperative API client for listing announcements (cross-site matching)
  const announcementsApi = createAnnouncementsApi(auth as Auth);

  const saveSlide = React.useCallback(
    async (data: SaveData) => {
      try {
        setIsSubmitting(true);

        // Current time for immediate publishing
        const now = new Date();

        // Determine start time based on publishNow preference
        let startShowingTime: Date;
        if (data.preferences.publishNow) {
          startShowingTime = now;
        } else {
          startShowingTime = setMinutes(
            setHours(
              new Date(data.preferences.startShowing),
              getHours(new Date(data.preferences.startShowingTime)),
            ),
            getMinutes(new Date(data.preferences.startShowingTime)),
          );
        }

        const baseAnnouncement = {
          title: data.description.title,
          content: data.description.content,
          startShowing: startShowingTime.toISOString(),
          stopShowing: setMinutes(
            setHours(
              new Date(data.preferences.stopShowing),
              getHours(new Date(data.preferences.stopShowingTime)),
            ),
            getMinutes(new Date(data.preferences.stopShowingTime)),
          ).toISOString(),
          location: null,
          displayDuration: data.preferences.displayDuration,
          eventStart: null,
          eventEnd: null,
          isSpecialEvent: false,
          videoUrl: null,
          eventType: 'Generic' as 'Generic' | 'SpecialEvent',
          allDay: false,
          siteId, // may be overridden for bulk create
          styleId: data.format.styleId,
          imageId: data.description.image?.id || null,
          backgroundId: data.format.background?.id,
          roomId: null,
          categoryId: data.format.category?.id || null,
          subcategoryId: data.format.subcategory?.id,
          showOnPrintCalendar: false,
          showOnDigitalSignage: true,
          eventTags: [],
          recurrenceId: null,
          secondaryImages:
            data.description.secondaryImages?.map((img: any) => img.id) || [],
          positionImageAboveWeatherBar:
            data.format.positionImageAboveWeatherBar,
        };

        // Bulk targeting logic
        const targetSiteIds: number[] = Array.from(
          new Set<number>(
            (data?.preferences?.targetSiteIds || [siteId]).filter(Boolean),
          ),
        );

        if (!existingAnnouncement?.id) {
          // CREATE
          if (bulkEnabled && targetSiteIds.length > 1) {
            const bulkId =
              (globalThis as any).crypto?.randomUUID?.() ||
              `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            const payload = targetSiteIds.map((sid) => ({
              ...baseAnnouncement,
              siteId: sid,
              bulkId,
            }));
            await createAnnouncementsBulk(payload as any);
          } else if (
            bulkEnabled &&
            targetSiteIds.length === 1 &&
            targetSiteIds[0] !== siteId
          ) {
            await createAnnouncement({
              ...baseAnnouncement,
              siteId: targetSiteIds[0],
            });
          } else {
            await createAnnouncement(baseAnnouncement);
          }
        } else {
          // UPDATE
          const applyAll =
            bulkEnabled && !!data?.preferences?.applyBulkEditToAll;
          const removedAnnouncements = applyAll
            ? (normalizedBulkGroup || []).filter(
                (announcement: any) =>
                  announcement &&
                  announcement.siteId &&
                  announcement.id !== existingAnnouncement?.id &&
                  !targetSiteIds.includes(announcement.siteId),
              )
            : [];

          if (applyAll && targetSiteIds.length > 1) {
            const candidateSites = (orgSites || []).filter((s: any) =>
              targetSiteIds.includes(s.id),
            );

            // Find matching announcements across target sites and update them
            const fetches = candidateSites.map((s: any) =>
              announcementsApi.getAnnouncements(s.id, {
                page: 0,
                pageSize: 200,
                status: 'all',
                sortField: 'updatedOn',
                sortOrder: 'desc',
                search: data.description.title || '',
              } as any),
            );

            const results = await Promise.all(fetches);
            const matches: any[] = [];
            for (const r of results) {
              const m = (r?.data || []).find(
                (a: any) =>
                  a.title === existingAnnouncement.title &&
                  a.styleId === existingAnnouncement.styleId &&
                  new Date(a.startShowing).toISOString() ===
                    new Date(existingAnnouncement.startShowing).toISOString() &&
                  new Date(a.stopShowing).toISOString() ===
                    new Date(existingAnnouncement.stopShowing).toISOString(),
              );
              if (m) matches.push(m);
            }
            if (!matches.find((m) => m.id === existingAnnouncement.id)) {
              matches.push(existingAnnouncement);
            }

            const matchesById = new Map(matches.map((m) => [m.id, m]));
            for (const announcement of normalizedBulkGroup) {
              if (
                targetSiteIds.includes(announcement.siteId) &&
                !matchesById.has(announcement.id)
              ) {
                matches.push(announcement);
                matchesById.set(announcement.id, announcement);
              }
            }

            const matchedSiteIds = matches.map((m) => m.siteId);
            const newSiteIds = targetSiteIds.filter(
              (id) => !matchedSiteIds.includes(id),
            );

            let effectiveBulkId =
              existingAnnouncement?.bulkAnnouncementId ||
              matches.find((m) => m?.bulkAnnouncementId)?.bulkAnnouncementId ||
              normalizedBulkGroup.find((m: any) => m?.bulkAnnouncementId)?.bulkAnnouncementId ||
              null;

            if (!effectiveBulkId && (matches.length > 1 || newSiteIds.length > 0)) {
              effectiveBulkId =
                (globalThis as any).crypto?.randomUUID?.() ||
                `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            }

            const announcementWithBulkId = effectiveBulkId
              ? { ...baseAnnouncement, bulkId: effectiveBulkId }
              : baseAnnouncement;

            const counts: BulkConfirmationCounts = {
              totalTargets: matches.length + newSiteIds.length,
              updatedCount: matches.length,
              createdCount: newSiteIds.length,
              removedCount: removedAnnouncements.length,
            };

            const fallbackMessageParts = [
              `Update ${counts.totalTargets} slides across selected locations?`,
            ];
            if (counts.createdCount) {
              fallbackMessageParts.push(
                `Adding to ${counts.createdCount} ${
                  counts.createdCount === 1 ? 'property' : 'properties'
                }`,
              );
            }
            if (counts.removedCount) {
              fallbackMessageParts.push(
                `Removing from ${counts.removedCount} ${
                  counts.removedCount === 1 ? 'property' : 'properties'
                }`,
              );
            }
            const fallbackMessage = fallbackMessageParts.join('\n');

            const confirmed = confirmBulkChanges
              ? await confirmBulkChanges(counts)
              : window.confirm(fallbackMessage);
            if (!confirmed) return;

            if (matches.length > 0) {
              const payload = matches.map((m) => ({
                id: m.id,
                ...announcementWithBulkId,
                siteId: m.siteId,
              }));
              await updateAnnouncementsBulk(payload as any);
            }

            if (newSiteIds.length > 0) {
              const createPayload = newSiteIds.map((sid) => ({
                ...announcementWithBulkId,
                siteId: sid,
              }));
              await createAnnouncementsBulk(createPayload as any);
            }
          } else {
            let effectiveBulkId = existingAnnouncement?.bulkAnnouncementId || null;
            if (!effectiveBulkId && targetSiteIds.length > 1) {
              effectiveBulkId =
                (globalThis as any).crypto?.randomUUID?.() ||
                `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            }
            const announcementWithBulkId = effectiveBulkId
              ? { ...baseAnnouncement, bulkId: effectiveBulkId }
              : baseAnnouncement;

            await updateAnnouncement({
              id: existingAnnouncement.id,
              ...announcementWithBulkId,
            });
          }

          if (applyAll && removedAnnouncements.length > 0) {
            await deleteAnnouncementsBulk(
              removedAnnouncements.map((announcement: any) => announcement.id),
            );
          }
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      siteId,
      existingAnnouncement,
      bulkEnabled,
      normalizedBulkGroup,
      orgSites,
      createAnnouncement,
      updateAnnouncement,
      createAnnouncementsBulk,
      updateAnnouncementsBulk,
      deleteAnnouncementsBulk,
      announcementsApi,
      confirmBulkChanges,
    ],
  );

  return { saveSlide, isSubmitting };
}
